/* ============================================================
   VITALCASA CRM — Dashboard
   Panel principal con tema claro/oscuro (del ThemeProvider),
   botones de módulos con glow, KPIs circulares y barras.
   ============================================================ */
import { useState, useEffect } from "react";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import usePermiso from "../hooks/usePermiso";

// ── Componente Donut SVG ───────────────────────────────────
const DonutChart = ({ valor, max, color, size = 120, strokeW = 10, isDark = false }) => {
  const r = (size - strokeW) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(valor / max, 1) : 0;
  const offset = circ * (1 - pct);
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={isDark ? "rgba(128,128,128,0.15)" : "rgba(0,0,0,0.10)"} strokeWidth={strokeW} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={strokeW}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.8s ease" }} />
    </svg>
  );
};

// ── Barra de progreso ──────────────────────────────────────
const ProgressBar = ({ valor, max, color, height = 8, isDark = false }) => {
  const pct = max > 0 ? Math.min((valor / max) * 100, 100) : 0;
  return (
    <div style={{ width: "100%", height, borderRadius: height, background: isDark ? "rgba(128,128,128,0.15)" : "rgba(0,0,0,0.10)", overflow: "hidden" }}>
      <div style={{ width: pct + "%", height: "100%", borderRadius: height, background: color, transition: "width 0.8s ease" }} />
    </div>
  );
};

// ── Barra segmentada (propiedades) ─────────────────────────
const SegmentBar = ({ segments, height = 10, isDark = false }) => {
  const total = segments.reduce((s, seg) => s + seg.valor, 0);
  return (
    <div style={{ display: "flex", width: "100%", height, borderRadius: height, overflow: "hidden", background: isDark ? "rgba(128,128,128,0.15)" : "rgba(0,0,0,0.10)" }}>
      {segments.map((seg, i) => (
        <div key={i} style={{ width: total > 0 ? (seg.valor / total * 100) + "%" : "0%", height: "100%", background: seg.color, transition: "width 0.8s ease" }} />
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";

  const [stats, setStats] = useState(null);
  const [hoverMod, setHoverMod] = useState(null);
  const { puede } = usePermiso();

  useReloadOnRouteEnter(() => {
    api.get("/dashboard/estadisticas")
      .then(res => setStats(res.data.data))
      .catch(err => console.error("Error cargando estadísticas:", err));
  });

  // ── Módulos ──────────────────────────────────────────────
  // Mismos nodeId que el menú/nav de AppLayout.jsx, para que un acceso directo
  // del Dashboard nunca lleve a un módulo al que el rol/usuario no tiene acceso
  // por el menú (si no hay permiso de "ver", tampoco se ofrece aquí el acceso directo).
  const modulos = [
    { label: "TAREAS",       ruta: "/tareas",       color: "#ef4444", nodeId: "tareas" },
    { label: "PIPELINE",     ruta: "/pipeline",      color: "#3b82f6", nodeId: "pipeline" },
    { label: "CONTACTOS",     ruta: "/contactos",      color: "#06b6d4", nodeId: "contactos" },
    { label: "PROPIEDADES",  ruta: "/propiedades",   color: "#8b5cf6", nodeId: "propiedades" },
    { label: "AGENDA",       ruta: "/agenda",        color: "#94a3b8", nodeId: "agenda" },
    { label: "BUSCADOR",     ruta: "/buscador",      color: "#f59e0b", nodeId: "buscador" },
    { label: "ANA",          ruta: "/ana",            color: "#10b981", nodeId: "ana" },
    { label: "GESTION VENTAS", ruta: "/gestion-ventas", color: "#e11d48", nodeId: "gestion_ventas" },
  ].filter((m) => puede(m.nodeId, "ver"));

  // ── Datos KPIs ───────────────────────────────────────────
  const totalLeads = Number(stats?.total_leads) || 0;
  const ventasMes = Number(stats?.cierres_mes) || 0;
  const visitasSemana = Number(stats?.visitas_semana) || 0;
  const conversion = totalLeads > 0 ? ((ventasMes / totalLeads) * 100).toFixed(1) : "0.0";

  const facturacionMes = Number(stats?.facturacion_mes) || 0;
  const objFacturacion = Number(stats?.objetivo_facturacion) || 30000;
  const pctFacturacion = objFacturacion > 0 ? Math.round((facturacionMes / objFacturacion) * 100) : 0;

  const totalProps = Number(stats?.total_propiedades) || 0;
  const propsVenta = Number(stats?.propiedades_venta) || 0;
  const propsAlquiler = Number(stats?.propiedades_alquiler) || 0;
  const propsExclusiva = Number(stats?.propiedades_exclusiva) || 0;
  const propsNuevas = Number(stats?.propiedades_nuevas) || 0;

  const tiempoCierre = Number(stats?.tiempo_medio_cierre) || 0;
  const objTiempoCierre = Number(stats?.objetivo_tiempo_cierre) || 25;

  // Fecha
  const hoy = new Date();
  const opcFecha = { weekday: "long", day: "numeric", month: "long", year: "numeric" };
  const fechaStr = hoy.toLocaleDateString("es-ES", opcFecha);
  const tareasPend = Number(stats?.tareas_pendientes) || 0;

  // ── Glow según tema ──────────────────────────────────────
  const cardGlow = isDark
    ? "0 0 60px 15px rgba(255,255,255,0.07), 0 0 120px 40px rgba(255,255,255,0.04), 0 0 200px 80px rgba(255,255,255,0.02)"
    : "0 0 60px 15px rgba(0,0,0,0.04), 0 0 120px 40px rgba(0,0,0,0.025), 0 0 200px 80px rgba(0,0,0,0.01)";

  const moduloGlow = (color) => isDark
    ? `0 0 30px 8px ${color}25, 0 0 60px 20px ${color}15`
    : `0 0 20px 6px ${color}20, 0 0 40px 15px ${color}10`;

  // ── Estilos ──────────────────────────────────────────────
  const s = {
    page: {
      minHeight: "calc(100vh - 52px)",
      background: t.bgApp,
      padding: "28px 36px",
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      transition: "background 0.3s",
    },
    // Módulos
    gridModulos: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      marginBottom: 28,
    },
    modulo: (i, color) => ({
      flex: "1 1 100px",
      minWidth: 100,
      padding: "12px 8px",
      borderRadius: 12,
      border: isDark
        ? `1px solid ${hoverMod === i ? color + "66" : t.border}`
        : `1px solid ${hoverMod === i ? color + "99" : t.border}`,
      borderLeft: `4px solid ${color}`,
      background: isDark
        ? t.bgCard
        : hoverMod === i ? color + "12" : t.bgCard,
      cursor: "pointer",
      textAlign: "center",
      transition: "all 0.2s ease",
      boxShadow: hoverMod === i ? moduloGlow(color) : "0 1px 3px rgba(0,0,0,0.06)",
      transform: hoverMod === i ? "translateY(-2px)" : "none",
    }),
    moduloLabel: (color) => ({
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.05em",
      color: isDark ? color : t.textPrimary,
      margin: 0,
      lineHeight: 1.25,
    }),
    // Saludo
    saludo: {
      marginBottom: 24,
    },
    saludoTitulo: {
      fontSize: 22,
      fontWeight: 700,
      color: t.textPrimary,
      margin: 0,
    },
    saludoSub: {
      fontSize: 13,
      color: t.textMuted,
      marginTop: 4,
    },
    // KPIs circulares
    gridDonut: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 16,
      marginBottom: 20,
    },
    donutCard: (accentColor) => ({
      background: t.bgCard,
      border: `1px solid ${t.border}`,
      borderLeft: isDark ? `1px solid ${t.border}` : `4px solid ${accentColor}`,
      borderRadius: 14,
      padding: "22px 18px",
      textAlign: "center",
      boxShadow: isDark ? cardGlow : "0 2px 8px rgba(0,0,0,0.07)",
      transition: "all 0.3s",
    }),
    donutWrap: {
      position: "relative",
      display: "inline-block",
      marginBottom: 12,
    },
    donutCenter: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(0deg)",
      textAlign: "center",
    },
    donutValor: {
      fontSize: 26,
      fontWeight: 800,
      color: t.textPrimary,
      lineHeight: 1,
    },
    donutSub: {
      fontSize: 10,
      color: t.textMuted,
      marginTop: 2,
    },
    donutLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: isDark ? t.textMuted : "#000000",
      marginBottom: 8,
    },
    badge: (color) => ({
      display: "inline-block",
      fontSize: 11,
      fontWeight: 700,
      padding: "3px 10px",
      borderRadius: 20,
      background: color + "22",
      color: color,
      border: `1px solid ${color}55`,
    }),
    // Barras
    gridBarras: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 16,
      marginBottom: 24,
    },
    barraCard: (accentColor) => ({
      background: t.bgCard,
      border: `1px solid ${t.border}`,
      borderLeft: isDark ? `1px solid ${t.border}` : `4px solid ${accentColor}`,
      borderRadius: 14,
      padding: "20px 22px",
      boxShadow: isDark ? cardGlow : "0 2px 8px rgba(0,0,0,0.07)",
      transition: "all 0.3s",
    }),
    barraHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 10,
    },
    barraLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: isDark ? t.textMuted : "#000000",
    },
    barraValor: {
      fontSize: 28,
      fontWeight: 800,
      color: t.textPrimary,
      margin: "4px 0",
    },
    barraObj: {
      fontSize: 12,
      color: isDark ? t.textMuted : t.textMuted,
      marginBottom: 12,
    },
    barraFooter: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: 11,
      color: t.textMuted,
      marginTop: 8,
    },
    segLegend: {
      display: "flex",
      gap: 16,
      marginTop: 10,
      fontSize: 11,
      color: t.textPrimary,
    },
    segDot: (color) => ({
      width: 8,
      height: 8,
      borderRadius: 4,
      background: color,
      display: "inline-block",
      marginRight: 5,
    }),
  };

  return (
    <div style={s.page}>
      {/* ── Saludo ───────────────────────────────── */}
      <div style={s.saludo}>
        <h2 style={s.saludoTitulo}>Buenos días, {usuario?.nombre?.split(" ")[0]} 👋</h2>
        <p style={s.saludoSub}>
          {fechaStr.charAt(0).toUpperCase() + fechaStr.slice(1)} · Tienes {tareasPend} tareas pendientes hoy
        </p>
      </div>

      {/* ── Módulos ──────────────────────────────── */}
      <div style={s.gridModulos}>
        {modulos.map((m, i) => (
          <div key={m.label} style={s.modulo(i, m.color)}
            onClick={() => navigate(m.ruta)}
            onMouseEnter={() => setHoverMod(i)}
            onMouseLeave={() => setHoverMod(null)}>
            <p style={s.moduloLabel(m.color)}>{m.label}</p>
          </div>
        ))}
      </div>

      
      {/* ── KPIs Circulares ──────────────────────── */}
      <div style={s.gridDonut}>
        {/* Leads activos */}
        <div style={s.donutCard("#3b82f6")}>
          <div style={s.donutWrap}>
            <DonutChart valor={totalLeads} max={Math.max(totalLeads, 50)} color="#3b82f6" isDark={isDark} />
            <div style={s.donutCenter}>
              <div style={s.donutValor}>{totalLeads}</div>
            </div>
          </div>
          <p style={s.donutLabel}>LEADS ACTIVOS</p>
          <span style={s.badge("#22c55e")}>↑ 12%</span>
        </div>

        {/* Ventas mes */}
        <div style={s.donutCard("#22c55e")}>
          <div style={s.donutWrap}>
            <DonutChart valor={ventasMes} max={10} color="#22c55e" isDark={isDark} />
            <div style={s.donutCenter}>
              <div style={s.donutValor}>{ventasMes}</div>
              <div style={s.donutSub}>/ 10 obj.</div>
            </div>
          </div>
          <p style={s.donutLabel}>VENTAS MES</p>
          <span style={s.badge("#22c55e")}>↑ +1 vs anterior</span>
        </div>

        {/* Visitas semana */}
        <div style={s.donutCard("#8b5cf6")}>
          <div style={s.donutWrap}>
            <DonutChart valor={visitasSemana} max={Math.max(visitasSemana, 20)} color="#8b5cf6" isDark={isDark} />
            <div style={s.donutCenter}>
              <div style={s.donutValor}>{visitasSemana}</div>
            </div>
          </div>
          <p style={s.donutLabel}>VISITAS SEMANA</p>
          <span style={s.badge("#ef4444")}>↓ -3 vs anterior</span>
        </div>

        {/* Conversión */}
        <div style={s.donutCard("#f59e0b")}>
          <div style={s.donutWrap}>
            <DonutChart valor={Number(conversion)} max={100} color="#f59e0b" isDark={isDark} />
            <div style={s.donutCenter}>
              <div style={s.donutValor}>{conversion}</div>
              <div style={s.donutSub}>%</div>
            </div>
          </div>
          <p style={s.donutLabel}>CONVERSIÓN</p>
          <span style={s.badge("#22c55e")}>↑ +0.8%</span>
        </div>
      </div>

      {/* ── Barras de progreso ───────────────────── */}
      <div style={s.gridBarras}>
        {/* Facturación */}
        <div style={s.barraCard("#3b82f6")}>
          <div style={s.barraHeader}>
            <span style={s.barraLabel}>FACTURACIÓN MES</span>
            <span style={s.badge("#22c55e")}>↑ {pctFacturacion > 0 ? pctFacturacion + "%" : "—"}</span>
          </div>
          <div style={{ ...s.barraValor, color: "#3b82f6" }}>
            {facturacionMes.toLocaleString("es-ES")} €
          </div>
          <div style={s.barraObj}>Objetivo: {objFacturacion.toLocaleString("es-ES")} €</div>
          <ProgressBar valor={facturacionMes} max={objFacturacion} color="#3b82f6" isDark={isDark} />
          <div style={s.barraFooter}>
            <span>0 €</span>
            <span>{pctFacturacion}%</span>
            <span>{objFacturacion.toLocaleString("es-ES")} €</span>
          </div>
        </div>

        {/* Propiedades en cartera */}
        <div style={s.barraCard("#f59e0b")}>
          <div style={s.barraHeader}>
            <span style={s.barraLabel}>PROPIEDADES EN CARTERA</span>
            <span style={s.badge("#22c55e")}>↑ {propsNuevas} nuevas</span>
          </div>
          <div style={{ ...s.barraValor, color: t.textPrimary }}>{totalProps}</div>
          <div style={s.barraObj}>
            {propsVenta} venta · {propsAlquiler} alquiler · {propsExclusiva} exclusivas
          </div>
          <SegmentBar height={10} isDark={isDark} segments={[
            { valor: propsVenta, color: "#f59e0b" },
            { valor: propsAlquiler, color: "#8b5cf6" },
            { valor: propsExclusiva, color: "#10b981" },
          ]} />
          <div style={s.segLegend}>
            <span><span style={s.segDot("#f59e0b")} />Venta</span>
            <span><span style={s.segDot("#8b5cf6")} />Alquiler</span>
            <span><span style={s.segDot("#10b981")} />Exclusiva</span>
          </div>
        </div>

        {/* Tiempo medio cierre */}
        <div style={s.barraCard("#10b981")}>
          <div style={s.barraHeader}>
            <span style={s.barraLabel}>TIEMPO MEDIO CIERRE</span>
            <span style={s.badge(tiempoCierre > objTiempoCierre ? "#ef4444" : "#22c55e")}>
              {tiempoCierre > objTiempoCierre ? "↑" : "↓"} {Math.abs(tiempoCierre - objTiempoCierre)} días
            </span>
          </div>
          <div style={{ ...s.barraValor, color: "#10b981" }}>{tiempoCierre} días</div>
          <div style={s.barraObj}>Objetivo: &lt; {objTiempoCierre} días</div>
          <ProgressBar
            isDark={isDark}
            valor={tiempoCierre}
            max={objTiempoCierre * 2}
            color={tiempoCierre <= objTiempoCierre
              ? "linear-gradient(90deg, #10b981, #22c55e)"
              : "linear-gradient(90deg, #10b981, #f59e0b, #ef4444)"}
            height={10}
          />
          <div style={s.barraFooter}>
            <span>0</span>
            <span>{objTiempoCierre > 0 ? Math.round((tiempoCierre / objTiempoCierre) * 100) : 0}%</span>
            <span>{objTiempoCierre} días</span>
          </div>
        </div>
      </div>
    </div>
  );
}